// Libs
import classNames from 'classnames/bind';
import { Button, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
// Components, Layouts, Pages
import ChildrenPage from '~/pages/common/childrenPage/ChildrenPage';
import { Loading } from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { getDetailCampaign } from '~/thunks/campaign/campaignThunk';
import { ICampaignDetail } from '~/utils/interface/campaign';
import { EMPTY_STRING } from '~/utils/constants/common';
import { StorageEnum } from '~/utils/enum/base';
import { RoleEnum } from '~/utils/enum/common';
import { adminRoute, adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRoute, staffRouteAbsolute } from '~/utils/routes/staff';
import { mockDataCampaignType } from '~/utils/mockData/campaign';
import { IBaseOption } from '~/utils/interface/common';
// Styles, images, icons
import styles from './CampaignDetail.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const CampaignDetailPage = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/campaign', 'common/base']);
  const { id: campaignId } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [campaignDetail, setCampaignDetail] = useState<ICampaignDetail>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentRole] = useState<string>(localStorage.getItem(StorageEnum.ROLE) || EMPTY_STRING);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!campaignId) return;

    handleGetCampaignDetail(campaignId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetCampaignDetail = (id: string) => {
    setIsLoading(true);

    dispatch(getDetailCampaign(id))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        setCampaignDetail(res?.data);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const resolveTypeName = (type: string) => {
    const option: IBaseOption = mockDataCampaignType.find((item) => item.value === type) as IBaseOption;

    return option?.label || EMPTY_STRING;
  };

  const handleBackToCampaigns = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRoute.base}${adminRoute.marketing}${adminRoute.campaigns}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRoute.base}${staffRoute.marketing}${staffRoute.campaigns}`);
        break;
    }
  };

  const handleEdit = () => {
    if (!campaignId) return;

    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.updateCampaign(campaignId));
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.updateCampaign(campaignId));
        break;
    }
  };
  //#endregion Handle Function

  return (
    <ChildrenPage
      id='campaignDetailPage'
      title={t('campaign_detail_title')}
      onBackToPreviousPage={handleBackToCampaigns}
    >
      <>
        <Stack direction={'column'} gap={'20px'} className={cx('container')}>
          <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-end'}>
            <Button
              variant='contained'
              startIcon={
                <img
                  src={icons.commonIconEditPrimary}
                  alt={t('common_alt_img', { ns: 'common/base' })}
                  className={cx('btnIcon')}
                />
              }
              onClick={handleEdit}
            >
              {t('campaign_detail_btn_edit')}
            </Button>
          </Stack>

          <Stack direction={'column'} gap={'20px'} className={cx('group')}>
            <Typography component={'h5'} className={cx('title')}>
              {t('campaign_detail_section_info')}
            </Typography>

            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={'24px'}>
              <Typography component={'p'} className={cx('key')}>
                {t('campaign_detail_label_name')}
              </Typography>
              <Typography component={'p'} className={cx('value')}>
                {campaignDetail?.name || EMPTY_STRING}
              </Typography>
            </Stack>

            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={'24px'}>
              <Typography component={'p'} className={cx('key')}>
                {t('campaign_detail_label_type')}
              </Typography>
              <Typography component={'p'} className={cx('value')}>
                {campaignDetail?.type ? resolveTypeName(campaignDetail.type) : EMPTY_STRING}
              </Typography>
            </Stack>

            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={'24px'}>
              <Typography component={'p'} className={cx('key')}>
                {t('campaign_detail_label_days')}
              </Typography>
              <Typography component={'p'} className={cx('value')}>
                {campaignDetail?.days || EMPTY_STRING}
              </Typography>
            </Stack>
          </Stack>

          <Stack direction={'column'} gap={'20px'} className={cx('group')}>
            <Typography component={'h5'} className={cx('title')}>
              {t('campaign_detail_section_message')}
            </Typography>

            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={'24px'}>
              <Typography component={'p'} className={cx('key')}>
                {t('campaign_detail_label_subject')}
              </Typography>
              <Typography component={'p'} className={cx('value')}>
                {campaignDetail?.subject || EMPTY_STRING}
              </Typography>
            </Stack>

            <Stack direction={'row'} alignItems={'flex-start'} justifyContent={'space-between'} gap={'24px'}>
              <Typography component={'p'} className={cx('key')}>
                {t('campaign_detail_label_content')}
              </Typography>
              <Typography component={'p'} className={cx('value', 'valueMultiLine')}>
                {campaignDetail?.content || EMPTY_STRING}
              </Typography>
            </Stack>
          </Stack>
        </Stack>

        {isLoading && <Loading />}
      </>
    </ChildrenPage>
  );
};

export default CampaignDetailPage;
